import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Description as NotesIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

interface CustomerCreateDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
    email?: string;
    phone?: string;
    notes?: string;
    customerTypeId?: string;
    zoneId?: string;
  };
  onSuccess?: (customer: any) => void;
}

export const CustomerCreateDialog: React.FC<CustomerCreateDialogProps> = ({
  open,
  onClose,
  initialValues = {},
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const [saving, setSaving] = useState(false);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [zones, setZones] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  const [formData, setFormData] = useState({
    displayName: '',
    firstName: '',
    lastName: '',
    company: '',
    industry: '',
    title: '',
    email: '',
    phone: '',
    notes: '',
    customerTypeId: '',
    zoneId: '',
    ...initialValues,
  });

  // Fetch reference data when dialog opens
  React.useEffect(() => {
    if (open) {
      const fetchReferenceData = async () => {
        setLoadingData(true);
        try {
          const [customerTypesResponse, zonesResponse] = await Promise.all([
            dataProvider.getList('crm/customer-types', {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'name', order: 'ASC' },
              filter: {},
            }),
            dataProvider.getList('crm/zones', {
              pagination: { page: 1, perPage: 100 },
              sort: { field: 'name', order: 'ASC' },
              filter: {},
            }),
          ]);
          
          setCustomerTypes(customerTypesResponse.data || []);
          setZones(zonesResponse.data || []);
        } catch (error) {
          console.error('Error fetching reference data:', error);
          notify('Error loading form data', { type: 'error' });
        } finally {
          setLoadingData(false);
        }
      };

      fetchReferenceData();
    }
  }, [open, dataProvider, notify]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};
    
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      const customerData = {
        ...formData,
        // Remove empty strings
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        company: formData.company || undefined,
        industry: formData.industry || undefined,
        title: formData.title || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        notes: formData.notes || undefined,
        customerTypeId: formData.customerTypeId || undefined,
        zoneId: formData.zoneId || undefined,
      };

      const { data } = await dataProvider.create('customers', { data: customerData });

      notify('Customer created successfully', { type: 'success' });
      refresh();
      
      if (onSuccess) {
        onSuccess(data);
      } else {
        redirect('show', 'customers', data.id);
      }
      
      onClose();
    } catch (error: any) {
      notify(error.message || 'Failed to create customer', { type: 'error' });
      console.error('Customer creation error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create New Customer
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Display Name */}
            <Grid item xs={12}>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'primary.main',
                  fontWeight: 'bold',
                }}
              >
                <BadgeIcon fontSize="small" />
                Customer Identifier
              </Typography>
              <TextField
                fullWidth
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                error={!!errors.displayName}
                helperText={errors.displayName || "A unique name to identify this customer (e.g., 'John Smith - Main St' or 'Acme Corp')"}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* Company Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" color="primary" />
                Company Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name (Optional)"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    helperText="Enter company name if this is a business customer"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Industry (Optional)"
                    value={formData.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    helperText="Business sector or industry"
                  />
                </Grid>
                
              </Grid>
            </Grid>

            {/* Point of Contact */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="primary" />
                Point of Contact
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title/Position"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon fontSize="small" color="primary" />
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Primary Phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone || "Include area code (e.g., +1 555-123-4567)"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Location & Zone */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="primary" />
                Location & Zone
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    loading={loadingData}
                    options={zones}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={zones.find((zone: any) => zone.id === formData.zoneId) || null}
                    onChange={(event, newValue) => handleInputChange('zoneId', newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Service Zone"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingData && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    loading={loadingData}
                    options={customerTypes}
                    getOptionLabel={(option: any) => option.name || ''}
                    value={customerTypes.find((ct: any) => ct.id === formData.customerTypeId) || null}
                    onChange={(event, newValue) => handleInputChange('customerTypeId', newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer Type"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingData && <CircularProgress color="inherit" size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotesIcon fontSize="small" color="primary" />
                Additional Information
              </Typography>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={4}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                helperText="Any additional information about this customer"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !formData.displayName}
        >
          {saving ? 'Creating...' : 'Create Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};